import { useState, useEffect } from 'react';
import { adminApi } from '../../lib/admin-api';
import toast from 'react-hot-toast';
import ModalContainer from '../shared/ModalContainer';
import Badge from '../shared/Badge';
import AccessSelector from '../shared/AccessSelector';
import { getThemeBadgeVariant } from '../../constants/subThemes';

export default function CreateInvitationModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);

  const [selectedRoles, setSelectedRoles] = useState(new Set());
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [productAccessLevels, setProductAccessLevels] = useState({});
  const [expiresAt, setExpiresAt] = useState(() => {
    // Default to 7 days from now
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().slice(0, 16); // Format for datetime-local input
  });

  const [invitationResult, setInvitationResult] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Fetch all roles and products
      const [rolesData, productsData] = await Promise.all([
        adminApi.users.getAllRoles(),
        adminApi.products.getOverview()
      ]);

      setAvailableRoles(rolesData.roles || []);
      setAvailableProducts(productsData.products || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load roles and products');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = (roleId) => {
    setSelectedRoles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roleId)) {
        newSet.delete(roleId);
      } else {
        newSet.add(roleId);
      }
      return newSet;
    });
  };

  const handleProductToggle = (productId) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
        // Remove access level when unchecked
        setProductAccessLevels(prev => {
          const newLevels = { ...prev };
          delete newLevels[productId];
          return newLevels;
        });
      } else {
        newSet.add(productId);
        // Default to read access when checked
        setProductAccessLevels(prev => ({ ...prev, [productId]: 'read' }));
      }
      return newSet;
    });
  };

  const handleProductAccessLevelChange = (productId, level) => {
    setProductAccessLevels(prev => ({ ...prev, [productId]: level }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedRoles.size === 0) {
      toast.error('Please select at least one role');
      return;
    }

    try {
      setSubmitting(true);

      const productAccess = Array.from(selectedProducts).map(productId => ({
        product_id: productId,
        access_level: productAccessLevels[productId] || 'read'
      }));

      // Calculate days until expiration
      const now = new Date();
      const expirationDate = new Date(expiresAt);
      const daysDiff = Math.ceil((expirationDate - now) / (1000 * 60 * 60 * 24));

      const result = await adminApi.invitations.create({
        role_ids: Array.from(selectedRoles),
        product_access: productAccess.length > 0 ? productAccess : undefined,
        expires_in_days: Math.max(1, daysDiff) // Ensure at least 1 day
      });

      setInvitationResult(result);
      toast.success('Invitation created successfully!');

      if (onSuccess) {
        onSuccess(result);
      }
    } catch (error) {
      console.error('Failed to create invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedRoles(new Set());
    setSelectedProducts(new Set());
    setProductAccessLevels({});
    // Reset to 7 days from now
    const date = new Date();
    date.setDate(date.getDate() + 7);
    setExpiresAt(date.toISOString().slice(0, 16));
    setInvitationResult(null);
    onClose();
  };

  const copyInviteLink = () => {
    const inviteUrl = `${window.location.origin}/register?token=${invitationResult.token}`;
    navigator.clipboard.writeText(inviteUrl);
    toast.success('Invitation link copied to clipboard!');
    // Auto-close modal after copying
    setTimeout(() => {
      handleClose();
    }, 500);
  };

  if (!isOpen) return null;

  // Success view
  if (invitationResult) {
    const inviteUrl = `${window.location.origin}/register?token=${invitationResult.token}`;
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={handleClose}
      >
        <div
          className="bg-gray-800 rounded-lg shadow-xl w-full border-2 border-purple-500/40 shadow-purple-900/20"
          style={{ maxWidth: '400px' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-5">
            {/* Centered Content */}
            <div className="pb-2">
              <div className="w-full space-y-3">
                {/* Success and Expiration Row */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-green-400 font-semibold">Success!</span>
                  </div>
                  <div className="text-xs text-gray-400">
                    Expires: {new Date(invitationResult.expires_at).toLocaleDateString()}
                  </div>
                </div>

                {/* Pretty Input */}
                <div className="relative">
                  <input
                    type="text"
                    value={inviteUrl}
                    readOnly
                    className="w-full pr-12 pl-4 py-3.5 bg-gradient-to-r from-gray-900 to-gray-800 border-2 border-purple-500/30 rounded-xl text-gray-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 shadow-lg shadow-purple-900/10 overflow-hidden transition-all"
                    style={{ color: 'var(--color-text-on-accent)' }}
                  />
                  <button
                    onClick={copyInviteLink}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-purple-400 transition-colors"
                    title="Copy to clipboard"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>

                {/* Helper Text */}
                <p className="text-xs text-center text-gray-400 mt-2">
                  Copy and share this link
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form view
  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg" style={{ color: 'var(--color-text-on-accent)' }}>
                      <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <span>Create User Invitation</span>
        </div>
      }
      isProcessing={submitting}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting || selectedRoles.size === 0}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            style={{ color: 'var(--color-text-on-accent)' }}
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-400 border-t-white"></div>
                <span>Creating...</span>
              </>
            ) : (
              <span>Create Invitation</span>
            )}
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="p-12 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-700 border-t-purple-500"></div>
        </div>
      ) : (
        <div className="p-6 space-y-5">
          {/* Access Selector */}
          <AccessSelector
            roles={availableRoles}
            items={availableProducts}
            selectedRoles={selectedRoles}
            selectedItems={selectedProducts}
            itemAccessLevels={productAccessLevels}
            onRoleToggle={handleRoleToggle}
            onItemToggle={handleProductToggle}
            onItemAccessLevelChange={handleProductAccessLevelChange}
            itemLabel="Products"
            disabled={submitting}
          />

          {/* Expiration Date & Time */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Expiration Date & Time
            </label>
            <div className="relative">
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-4 py-2 bg-gray-800/40 border border-gray-600/50 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-purple-500/50 focus:border-purple-500/50 [color-scheme:dark]"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Invitation will expire on {new Date(expiresAt).toLocaleString()}
            </p>
          </div>
        </div>
      )}
    </ModalContainer>
  );
}
